/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useRef, useState } from "react";

import cn from "classnames";
import { useLocation } from "react-router-dom";

import { useDetectClickOutside } from "utils/useDetectClickOutside";

import useDevice from "hooks/useDevice";
import { useUI } from "state";

import { ChevronDown } from "components/icons";

import s from "./SidebarMenuList.module.css";

import { getFlag } from "state/FeaturesFlagContext";
import Stack from "../Stack";
import Collapsible from "../Collapsible";
import { LinkItem } from "../Button";
import Text from "../Text";

const SidebarMenuList = ({
  menuDetails = {
    title: "",
    Icon: () => null,
  },
  isListOpen,
  onMenuToggle,
  list,
  index,
}) => {
  const listRef = useRef();
  const { isSidebarShrunk: isShrunk } = useUI();
  const isOpen = isListOpen;
  const [isHovered, setIsHovered] = useState(false);
  useDetectClickOutside(listRef, () => {
    if (isHovered) setIsHovered(false);
  });

  return (
    <>
      <div
        ref={listRef}
        className="px-s15 relative flex flex-col w-full "
        onMouseEnter={() => {
          setIsHovered(true);
          if (isShrunk) onMenuToggle(menuDetails.title);
        }}
        onClick={() => {
          setIsHovered(true);
          if (isShrunk) onMenuToggle(menuDetails.title);
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          onClick={() => onMenuToggle(menuDetails.title)}
          test-id={`sidebar-menu-item-${index}`}
        >
          <div
            className={cn(
              "flex items-center duration-200 cursor-pointer w-full",
              {
                "text-c-gray-50": isOpen,
                "bg-primary-600": isOpen && isShrunk,
                "text-c-gray-400 bg-c-gray-700 hover:bg-c-gray-600": !isOpen,
              },
              isShrunk
                ? "p-s10 py-s15 justify-center"
                : "px-s10 py-s15 justify-between ",
            )}
          >
            <div className="flex items-center space-x-3 w-full">
              <menuDetails.Icon
                className="!min-w-[20px] ml-[3px]"
                isActive={isOpen}
              />

              <span
                className={cn("text-nowrap", { "overflow-hidden": isShrunk })}
              >
                {menuDetails.title}
              </span>
            </div>

            <div className="w-4 h-4">
              <ChevronDown
                className={cn(
                  "duration-300 w-full h-full",
                  isOpen ? "rotate-180" : "rotate-0",
                )}
              />
            </div>
          </div>
        </div>

        <Stack
          className={cn(
            "border-b border-secondary pt-s5 mb-s5",
            isOpen && !isShrunk && "pb-s5",
          )}
        >
          {isShrunk ? (
            isHovered ? (
              <div className="absolute left-[80%] top-0 w-48">
                <ListContent
                  list={list}
                  isFloating={true}
                  index={index}
                  test-id={`sidebar-menu-list-${index}`}
                />
              </div>
            ) : null
          ) : (
            <Collapsible isOpen={isOpen}>
              <ListContent
                list={list}
                isFloating={false}
                test-id={`sidebar-menu-list-${index}`}
                index={index}
              />
            </Collapsible>
          )}
        </Stack>
      </div>
    </>
  );
};

const ListContent = ({ list, isFloating, index }) => {
  const { pathname } = useLocation();

  const {
    toggleSidebar,
    currentRouteOptions,
    setSidebarShrunk,
    isSidebarShrunk,
  } = useUI();

  const { isDesktop, isMobile } = useDevice();
  return (
    <ul className={cn(isFloating && "bg-c-gray-900 p-2")}>
      {list.map(({ slug, name, id, feature_flag }, key) => {
        // this logic should handle nested routing for e.g /explore/:id
        if (!!feature_flag && getFlag(feature_flag) === false) return <></>;

        const removeSlash = (str) => str.slice(1);
        const pathWithoutSlash = removeSlash(pathname);
        const slugWithoutSlash = removeSlash(slug);

        const isNestedRoute =
          pathWithoutSlash.includes(slugWithoutSlash) &&
          pathWithoutSlash.includes("/");

        const isActive = slug === pathname || isNestedRoute;
        return (
          <li key={slug} className={cn({ "ml-[10px]": !isSidebarShrunk })}>
            <LinkItem
              id={id}
              slug={slug}
              variant="ghost"
              className={cn(
                s.linkItem,
                isFloating ? "px-s15 py-s10" : "px-s10 py-s15",
              )}
              isActive={isActive}
              onClick={(e) => {
                // To close the sidebar on mobile
                toggleSidebar(false);

                // check if the route is nested ('users/groups/1') and currentRouteOptions is equal to the root route ('/users')
                if (currentRouteOptions.route === slug) {
                  if (currentRouteOptions.callback) {
                    e.preventDefault();
                    currentRouteOptions.callback();
                  }
                }

                if (!isSidebarShrunk) {
                  if (isDesktop || isMobile) return;
                  setSidebarShrunk(true);
                }
              }}
              test-id={`sidebar-menu-item-${index}-link-${key}`}
            >
              <div className={cn("flex items-center space-x-4 lg:space-x-3")}>
                <div
                  className={cn(
                    s.indicator,
                    isActive ? "!bg-c-gray-50" : "bg-c-gray-600 ",
                  )}
                />

                <Text>{name}</Text>
              </div>
            </LinkItem>
          </li>
        );
      })}
    </ul>
  );
};

export default SidebarMenuList;
